/**
 * Composant Category Breakdown
 * Affiche la répartition du CA par catégorie
 */

import {
  Component,
  OnInit,
  input,
  inject,
  DestroyRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AnalyticsService,
  CategoryBreakdown,
  PeriodType,
} from '../../../../services/analytics.service';
import { LogService } from '../../../../../services/log.service';

@Component({
  selector: 'app-category-breakdown',
  imports: [CommonModule],
  templateUrl: './category-breakdown.component.html',
  styleUrls: ['./category-breakdown.component.css'],
})
export class CategoryBreakdownComponent implements OnInit {
  private log = inject(LogService);
  private analyticsService = inject(AnalyticsService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  readonly period = input<PeriodType>('today');

  categories: CategoryBreakdown[] = [];
  isLoading = true;
  error: string | null = null;
  totalRevenue = 0;

  // Couleurs pour les catégories
  private colors = [
    'bg-indigo-500',
    'bg-emerald-500',
    'bg-amber-500',
    'bg-rose-500',
    'bg-cyan-500',
    'bg-purple-500',
  ];

  ngOnInit(): void {
    this.loadCategories();
  }

  loadCategories(): void {
    this.isLoading = true;
    this.error = null;

    this.analyticsService
      .getCategoryBreakdown(this.period())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.categories = data;
          this.totalRevenue = data.reduce((sum, c) => sum + c.revenue, 0);
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.log.error('Erreur chargement catégories:', err);
          this.error = 'Impossible de charger les données';
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  getCategoryIcon(category: string): string {
    const icons: Record<string, string> = {
      Plats: '🍔',
      Boissons: '🥤',
      Desserts: '🍰',
      Snacks: '🍟',
      Entrées: '🥗',
      Autre: '📦',
    };
    return icons[category] || '📦';
  }

  getColor(index: number): string {
    return this.colors[index % this.colors.length];
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  formatPercentage(value: number): string {
    return `${Math.round(value)}%`;
  }

  // Pour le donut chart SVG
  getDonutSegments(): { offset: number; length: number; color: string }[] {
    const segments: { offset: number; length: number; color: string }[] = [];
    let currentOffset = 25; // Commence à 12h (25% de décalage)

    const svgColors = [
      '#6366f1', // indigo
      '#10b981', // emerald
      '#f59e0b', // amber
      '#f43f5e', // rose
      '#06b6d4', // cyan
      '#a855f7', // purple
    ];

    this.categories.forEach((cat, index) => {
      segments.push({
        offset: currentOffset,
        length: cat.percentage,
        color: svgColors[index % svgColors.length],
      });
      currentOffset += cat.percentage;
    });

    return segments;
  }
}
